import { useEffect, useRef, useState } from 'react';
import { Ghost, UserRound, WandSparkles, ArrowRight, Mail, LockKeyhole, Eye, EyeOff } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import BrandGhostie from '../components/BrandGhostie';
import { PhoneCodeSelect } from '../lib/phoneCodes';

const GENDERS = ['Man', 'Woman', 'Prefer not to say'];

export default function Login({ mode = 'login' }) {
  const buttonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const isSignup = mode === 'signup' || location.pathname === '/signup';
  const isTeam = location.pathname === '/team';
  const [signupChoice, setSignupChoice] = useState('');
  const [form, setForm] = useState({
    first_name: '', last_name: '', gender: '', date_of_birth: '',
    email: '', password: '', phone_code: '+1', phone: '', accepted_terms: false,
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const goFor = (user) => {
    if (user.role === 'admin') navigate(isTeam ? '/team/content' : '/admin');
    else if (user.role === 'host') navigate('/host');
    else navigate('/dashboard');
  };

  const googlePayload = () => ({
    role: isSignup ? 'customer' : undefined,
    first_name: form.first_name,
    last_name: form.last_name,
    gender: form.gender,
    date_of_birth: form.date_of_birth,
    phone: `${form.phone_code} ${form.phone}`.trim(),
  });

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setError('Google login is not configured.');
      return;
    }
    const initialize = () => {
      if (!buttonRef.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            if (isSignup && signupChoice !== 'traveller') {
              setError('Choose Traveller first, or use the Ghostie application page.');
              return;
            }
            if (isSignup && !form.accepted_terms) {
              setError('Please accept the terms and conditions first.');
              return;
            }
            const { data } = await api.post('/auth/google', { credential, ...googlePayload() });
            await refresh();
            goFor(data.user);
          } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Google login failed. Please try again.');
          }
        },
      });
      buttonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'filled_black', size: 'large', shape: 'pill', width: 320,
        text: isSignup ? 'signup_with' : 'signin_with',
      });
    };
    if (window.google?.accounts?.id) { initialize(); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = initialize;
    document.head.appendChild(script);
    return () => script.remove();
  }, [navigate, refresh, isSignup, signupChoice, form.first_name, form.last_name, form.gender, form.date_of_birth, form.phone_code, form.phone, form.accepted_terms]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (isSignup && !form.accepted_terms) {
      setError('Please accept the terms and conditions.');
      return;
    }
    try {
      const payload = isSignup
        ? {
            role: 'customer',
            name: `${form.first_name} ${form.last_name}`.trim(),
            first_name: form.first_name,
            last_name: form.last_name,
            gender: form.gender,
            date_of_birth: form.date_of_birth,
            email: form.email,
            password: form.password,
            phone: `${form.phone_code} ${form.phone}`.trim(),
          }
        : { email: form.email, password: form.password };
      const { data } = await api.post(isSignup ? '/auth/signup' : '/auth/login', payload);
      await refresh();
      goFor(data.user);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Something went wrong.');
    }
  };

  const SignupChoice = () => (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="text-center mb-10">
        <div className="badge-cozy mb-4">Choose your doorway</div>
        <h1 className="text-5xl sm:text-6xl font-heading font-semibold mb-4">Join <BrandGhostie /></h1>
        <p className="text-[#a3a3a3] max-w-2xl mx-auto leading-relaxed">Are you here to wander the strange side of a city, or to become the local Ghostie who opens the door?</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <button type="button" onClick={()=>setSignupChoice('traveller')} className="magical-card p-8 sm:p-10 text-left min-h-[320px] border-2 hover:border-[#f97316] transition-colors group">
          <UserRound size={52} className="text-[#f97316] mb-6"/>
          <h2 className="font-heading text-4xl mb-4">Traveller</h2>
          <p className="text-[#c7c7c7] leading-relaxed mb-8">Create your account, book Ghostie experiences, manage active bookings and keep your reviews in one cozy little dashboard.</p>
          <span className="btn-primary inline-flex">Create Traveller account <ArrowRight size={16}/></span>
        </button>
        <Link to="/start" className="magical-card p-8 sm:p-10 text-left min-h-[320px] border-2 hover:border-[#f97316] transition-colors group">
          <WandSparkles size={52} className="text-[#f97316] mb-6"/>
          <h2 className="font-heading text-4xl mb-4">Ghostie Host</h2>
          <p className="text-[#c7c7c7] leading-relaxed mb-8">Apply to become a local Ghostie, tell us where your city gets weird, and continue the host application from the dedicated page.</p>
          <span className="btn-primary inline-flex">Start Host application <ArrowRight size={16}/></span>
        </Link>
      </div>
    </div>
  );

  if (isSignup && !signupChoice) return <SignupChoice />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-[1fr_1.1fr] gap-8 items-start">
      <div className="magical-card p-8">
        <Ghost size={56} className="text-[#f97316] mb-4 float-anim"/>
        <div className="badge-cozy mb-4">{isTeam ? 'Hidden team portal' : isSignup ? 'Traveller account' : 'Welcome back'}</div>
        <h1 className="text-4xl sm:text-5xl font-heading font-semibold mb-4">
          {isTeam ? 'Team login' : isSignup ? <>Create Traveller account</> : <>Enter <BrandGhostie /></>}
        </h1>
        <p className="text-[#a3a3a3] leading-relaxed">
          {isTeam
            ? 'Private admin access for approved team members. Google admin login also works here.'
            : isSignup
              ? 'Set up your Traveller profile. ID verification will happen later in your dashboard before booking.'
              : 'Log in with email/password or Google to see your bookings, host profile, or HQ.'}
        </p>
        {isSignup && <button type="button" onClick={()=>setSignupChoice('')} className="chip mt-5">← Change account type</button>}
      </div>

      <div className="magical-card p-6 sm:p-8">
        <form onSubmit={submit} className="space-y-4">
          {isSignup && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label">Name</label><input required className="input-cozy" value={form.first_name} onChange={e=>setForm(s=>({...s,first_name:e.target.value}))}/></div>
                <div><label className="label">Last name</label><input required className="input-cozy" value={form.last_name} onChange={e=>setForm(s=>({...s,last_name:e.target.value}))}/></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="label">Gender</label><select required className="input-cozy" value={form.gender} onChange={e=>setForm(s=>({...s,gender:e.target.value}))}><option value="">Select</option>{GENDERS.map(g=><option key={g}>{g}</option>)}</select></div>
                <div><label className="label">Date of birth</label><input required type="date" className="input-cozy" value={form.date_of_birth} onChange={e=>setForm(s=>({...s,date_of_birth:e.target.value}))}/></div>
              </div>
            </>
          )}
          {isSignup && (
            <div>
              <label className="label">Phone number</label>
              <div className="grid grid-cols-[155px_minmax(0,1fr)] gap-3 max-w-xl">
                <PhoneCodeSelect value={form.phone_code} onChange={e=>setForm(s=>({...s,phone_code:e.target.value}))} className="input-cozy" />
                <input required placeholder="Phone number" value={form.phone} onChange={e=>setForm(s=>({...s,phone:e.target.value}))} className="input-cozy"/>
              </div>
            </div>
          )}

          <section className="rounded-3xl border border-[#3f2e1e] bg-[#111]/45 p-5 sm:p-7 space-y-5">
            <div className="text-center">
              <div className="text-xl font-semibold text-[#fafafa] mb-4">Sign in with</div>
              <div ref={buttonRef} className="flex justify-center" data-testid="login-google" />
            </div>

            <div className="flex items-center gap-4 text-sm font-bold text-[#a3a3a3]">
              <span className="h-px bg-[#3f2e1e] flex-1" /> Or <span className="h-px bg-[#3f2e1e] flex-1" />
            </div>

            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input required type="email" className="input-cozy pl-12" value={form.email} onChange={e=>setForm(s=>({...s,email:e.target.value}))}/>
              </div>
            </div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <LockKeyhole size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input required type={showPassword ? 'text' : 'password'} minLength={8} className="input-cozy pl-12 pr-12" value={form.password} onChange={e=>setForm(s=>({...s,password:e.target.value}))}/>
                <button type="button" onClick={()=>setShowPassword(v=>!v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#a3a3a3]">
                  {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>
            {isSignup && (
              <label className="flex items-start gap-3 text-sm text-[#c7c7c7]">
                <input required type="checkbox" checked={form.accepted_terms} onChange={e=>setForm(s=>({...s,accepted_terms:e.target.checked}))} className="mt-1 accent-[#f97316]" data-testid="traveller-terms"/>
                <span>Accetto <Link to="/legal/terms" className="text-[#f97316] hover:underline">termini e condizioni</Link>.</span>
              </label>
            )}
            <button className="btn-primary w-full justify-center">{isSignup ? 'Create Traveller account' : 'Login'}</button>
          </section>
        </form>
        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
        {!isTeam && (
          <p className="text-sm text-[#a3a3a3] mt-6 text-center">
            {isSignup ? 'Already have an account?' : 'New here?'}{' '}
            <Link to={isSignup ? '/login' : '/signup'} className="text-[#f97316] font-semibold">
              {isSignup ? 'Login' : 'Sign Up'}
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
